import { ExternalLink } from 'lucide-react';
import type { ScoutingViewRow } from './scoutingView';
import { displayScoutingDate, safeScoutingUrl, scoutingCheckStatus } from './scoutingDetails';

export function ScoutingAccounts({ row }: { row: ScoutingViewRow }) {
  const accounts = row.details?.accounts ?? [];
  return (
    <div className="tw-mt-1 tw-space-y-0.5 tw-text-[11px] tw-text-muted-foreground">
      {accounts.map((account) => {
        const href = safeScoutingUrl(account.profileUrl);
        return (
          <div
            key={`${account.platform}:${account.handle}`}
            title={`Observed: ${displayScoutingDate(account.observedAt)}`}
          >
            {account.platform}:{' '}
            {href ? (
              <a className="hover:tw-underline" href={href} target="_blank" rel="noopener noreferrer">
                @{account.handle} <ExternalLink className="tw-inline tw-h-3 tw-w-3" />
              </a>
            ) : (
              `@${account.handle}`
            )}
          </div>
        );
      })}
      <div>Joined campaign {displayScoutingDate(row.details?.appliedAt)}</div>
    </div>
  );
}
export function ScoutingCoverage({ row }: { row: ScoutingViewRow }) {
  const social = row.details?.social;
  return (
    <details className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
      <summary className="tw-cursor-pointer">Data sources</summary>
      <div className="tw-w-40 tw-whitespace-normal tw-space-y-1 tw-py-1">
        <p>
          Social: {social?.platform ?? '—'} · observed {displayScoutingDate(social?.observedAt)}
        </p>
        <p>{social?.reason ?? 'Scouting details unavailable.'}</p>
        <p>Campaign registration: enrollment joinedAt</p>
        <p>Contact: {row.details?.contact.source ?? 'unavailable'}</p>
        <p>
          Score: {row.details?.score?.freshness ?? 'unavailable'} · calculated{' '}
          {displayScoutingDate(row.details?.score?.calculatedAt)}
        </p>
        <p>
          Formula: {row.details?.score?.formulaVersion ?? '—'} · revision {row.details?.score?.revision ?? '—'} · data
          through {displayScoutingDate(row.details?.score?.inputCutoff)}
        </p>
      </div>
    </details>
  );
}
export function ScoutingChecks({ row }: { row: ScoutingViewRow }) {
  const checks = row.details?.checks ?? [];
  const state = scoutingCheckStatus(row);
  const label =
    state === 'review' ? 'Needs review' : state === 'clear' ? 'No flags in checked sources' : 'Checks unavailable';
  const tone =
    state === 'review' ? 'tw-text-amber-800' : state === 'clear' ? 'tw-text-emerald-700' : 'tw-text-muted-foreground';
  return (
    <details className="tw-min-w-44 tw-text-xs">
      <summary className={`tw-cursor-pointer tw-font-medium ${tone}`}>{label}</summary>
      {checks.length ? (
        <ul className="tw-mt-2 tw-max-h-56 tw-space-y-2 tw-overflow-y-auto tw-whitespace-normal">
          {checks.map((check) => (
            <li key={check.code}>
              <strong>
                {check.status === 'review' ? 'Review' : check.status === 'clear' ? 'Clear' : 'Unavailable'}:{' '}
              </strong>
              {check.message}
              <div className="tw-text-[10px] tw-text-muted-foreground">Source: {check.source}</div>
              {check.evidence.length > 0 && <div className="tw-break-words">{check.evidence.join('; ')}</div>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="tw-mt-1">No check results were returned. This is not a passed check.</p>
      )}
    </details>
  );
}
export function ScoutingAnswers({ row }: { row: ScoutingViewRow }) {
  const answers = row.details?.application?.answers ?? [];
  if (!answers.length) {
    return <span className="tw-text-muted-foreground">Unavailable</span>;
  }
  return (
    <details className="tw-min-w-40 tw-text-xs">
      <summary className="tw-cursor-pointer">View {answers.length} answers</summary>
      <dl className="tw-mt-2 tw-max-h-56 tw-space-y-2 tw-overflow-y-auto tw-whitespace-normal">
        {answers.map((answer) => (
          <div key={answer.key}>
            <dt className="tw-font-medium">{answer.label}</dt>
            <dd className="tw-whitespace-pre-wrap tw-break-words">{answer.value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
